// The plot line of the context sheet (v2 slot 3; ui-map-interface.md §12.0
// "The plot line"; mockup round 088 card 08; R1795 the flag `routes`;
// R2589 the loop parts).
//
// An order of v2 holds several legs, and a loop part repeats
// (`v2-the-deep.md` §7, R2549). While a course is plotted, the context
// sheet stands in its third state, `plot`: the plot line in place of the
// peek line (PLOT · n LEGS, the fleet and the length, ✕), and the row
// LOOP ▢ · UNDO · DONE. Raised, the sheet lists the legs as 44 px rows
// with a drag grip and ✕, a bar at the left edge on the legs of a loop
// part, and after each part the leg back to its opening point with the
// pass count. A desktop card shows a LEGS section and the plot row in
// place of the order row.
//
// **The loop parts** (§7 rules 1 to 5). `PlotDraft.loop` is the LOOP flag
// of the plot line. Each leg that the commander adds while LOOP is on
// joins a loop part (`PlotLeg.loop`). The leg that ends a part carries
// its pass count in `passes`; the last part with 0 has no end. That is
// the encoding of the wire (`Waypoint`, R2576), so the draft is the wire.
// A part closes when the commander adds a leg with LOOP off after it, or
// adds a leg after LOOP went off and on again: the part then takes
// `route_loop_passes_default`.
//
// **The plot starts at its first leg** (R2729). The run from the fleet to
// the first leg is the joining run: the fleet sails it before the plot,
// and it is no part of the plot. The first leg therefore never joins a
// loop part, and a part that starts at the second leg opens at the first
// leg. The draft keeps `from` for the joining run alone, so a drifting
// hull never moves the plot.
//
// The state exists only while the flag `routes` holds. Everything here is
// pure: every function answers a new draft and never mutates its input.

import { card as S } from '../gen/strings';
import { num } from '../state';
import { KEYS } from '../gen/presentation';
import { buttonHtml, type Action, type CardEnv, type CardSpec } from './cards/card';
import { sheetCloseHtml } from './ctxsheet';
import { cutRunes, runeCount, tidySpaces } from '../util/text';
import { esc } from './esc';

/** A point in world px. */
export interface PlotPoint { x: number; y: number }

/** One leg of a plotted course, in world px, as the wire sends it. */
export interface PlotLeg extends PlotPoint {
  /** The leg joined a loop part: LOOP was on when it was added. */
  loop: boolean;
  /** The pass count of the part this leg ends, 1 to the cap; 0 on every
   *  other leg, and on the last leg of a part with no end. */
  passes: number;
  /** Adding this leg closed the part before it, so UNDO opens it again. */
  closes?: boolean;
}

/** The caps of a course, from `CatalogRules` (R2576). */
export interface PlotCaps {
  /** The most legs of one course (`route_legs_max`); 0 is no cap. */
  legsMax: number;
  /** The highest pass count (`route_loop_passes_max`). */
  passesMax: number;
  /** The pass count a part takes when it closes (`route_loop_passes_default`). */
  passesDefault: number;
}

/** The caps before the hello names them: the figures of §7. */
const CAPS_UNTIL_HELLO: PlotCaps = { legsMax: 0, passesMax: 9, passesDefault: 2 };

/** A course while it is plotted: the fleet, where it stands, the legs, the
 *  LOOP flag and the caps. */
export interface PlotDraft {
  fleetId: number;
  name: string;
  from: PlotPoint;
  legs: PlotLeg[];
  /** The LOOP flag of the plot line. */
  loop: boolean;
  /** The leg count when LOOP last went on: a loop leg added at that
   *  count opens a new part at the last leg (§7 rule 2). */
  loopAt: number;
  caps: PlotCaps;
}

/** The caps of the catalog, with the figures of §7 for a cap it does not
 *  state yet. */
export function plotCaps(rules: { routeLegsMax: number; routeLoopPassesMax: number; routeLoopPassesDefault: number }): PlotCaps {
  const passesDefault = rules.routeLoopPassesDefault > 0 ? rules.routeLoopPassesDefault : CAPS_UNTIL_HELLO.passesDefault;
  return {
    legsMax: rules.routeLegsMax,
    passesMax: Math.max(passesDefault, rules.routeLoopPassesMax > 0 ? rules.routeLoopPassesMax : CAPS_UNTIL_HELLO.passesMax),
    passesDefault,
  };
}

/** A new course for one fleet. LOOP is on when plot mode opens (R1823), so
 *  a plot that only adds legs is one loop part with no end: the patrol of
 *  today with more legs. */
export function plotStart(fleetId: number, name: string, from: PlotPoint, caps: PlotCaps = CAPS_UNTIL_HELLO): PlotDraft {
  return { fleetId, name, from: { x: from.x, y: from.y }, legs: [], loop: true, loopAt: 0, caps };
}

/** The draft holds the most legs a course may hold. */
function plotFull(d: PlotDraft): boolean {
  return d.caps.legsMax > 0 && d.legs.length >= d.caps.legsMax;
}

/**
 * A tap on the map adds a leg. The leg takes the LOOP flag. A loop part
 * that stands open before it closes with the default pass count when the
 * new leg is not a loop leg, or when LOOP went on again since the last
 * leg (a new part opens at the last leg). A full draft adds nothing.
 */
export function plotAdd(d: PlotDraft, at: PlotPoint): PlotDraft {
  if (plotFull(d)) return d;
  const legs = d.legs.slice();
  const prev = legs[legs.length - 1];
  // The first leg starts the plot and joins no loop part (R2729).
  const leg: PlotLeg = { x: at.x, y: at.y, loop: d.loop && legs.length > 0, passes: 0 };
  if (prev && prev.loop && prev.passes === 0 && (!d.loop || d.loopAt === legs.length)) {
    legs[legs.length - 1] = { ...prev, passes: d.caps.passesDefault };
    leg.closes = true;
  }
  legs.push(leg);
  return { ...d, legs };
}

/** UNDO takes the last leg away, and the part it empties with it. A part
 *  that the leg closed opens again. */
export function plotUndo(d: PlotDraft): PlotDraft {
  if (d.legs.length === 0) return d;
  const gone = d.legs[d.legs.length - 1];
  const legs = d.legs.slice(0, -1);
  if (gone.closes && legs.length > 0) legs[legs.length - 1] = { ...legs[legs.length - 1], passes: 0 };
  return { ...d, legs, loopAt: Math.min(d.loopAt, legs.length) };
}

/**
 * Makes the legs a course the hub takes again after a row moved or went
 * (§14.2 "The hub refuses"): the first leg joins no loop part (R2729), a
 * leg that is no loop leg carries no pass count, a loop part with a leg
 * after it carries a count, and a count stays inside the cap.
 */
function settle(legs: PlotLeg[], caps: PlotCaps): PlotLeg[] {
  return legs.map((l, i) => {
    if (i === 0) return { x: l.x, y: l.y, loop: false, passes: 0 };
    const next = legs[i + 1];
    let passes = l.loop ? Math.min(l.passes, caps.passesMax) : 0;
    if (l.loop && passes === 0 && next && !next.loop) passes = caps.passesDefault;
    return { x: l.x, y: l.y, loop: l.loop, passes };
  });
}

/** ✕ of a row takes that leg away. */
export function plotRemove(d: PlotDraft, i: number): PlotDraft {
  if (i < 0 || i >= d.legs.length) return d;
  const legs = settle(d.legs.filter((_, k) => k !== i), d.caps);
  return { ...d, legs, loopAt: Math.min(d.loopAt, legs.length) };
}

/** A drag on a grip moves one leg to another place in the list. The leg
 *  keeps its part flag, and the counts settle again. */
export function plotMove(d: PlotDraft, from: number, to: number): PlotDraft {
  const n = d.legs.length;
  if (from < 0 || from >= n || to < 0 || to >= n || from === to) return d;
  const legs = d.legs.slice();
  const [leg] = legs.splice(from, 1);
  legs.splice(to, 0, leg);
  return { ...d, legs: settle(legs, d.caps) };
}

/** LOOP flips the flag of the plot line. Turning it on notes the leg
 *  count, so the next loop leg opens a new part at the last leg. */
export function plotLoop(d: PlotDraft): PlotDraft {
  return d.loop ? { ...d, loop: false } : { ...d, loop: true, loopAt: d.legs.length };
}

/** A tap on the pass count of a closed part steps it from 1 to the cap
 *  and back to 1. `i` is the leg that ends the part. */
export function plotPasses(d: PlotDraft, i: number): PlotDraft {
  const leg = d.legs[i];
  if (!leg || !leg.loop || leg.passes === 0) return d;
  const legs = d.legs.slice();
  legs[i] = { ...leg, passes: leg.passes >= d.caps.passesMax ? 1 : leg.passes + 1 };
  return { ...d, legs };
}

/** One loop part: the index of the leg it opens at, its first and last
 *  leg and its pass count (0 is no end). */
export interface LoopPart { open: number; first: number; last: number; passes: number }

/** The loop parts of a list of legs, in leg order: a run of loop legs
 *  ends at a leg with a pass count or at a leg before a leg that is no
 *  loop leg or at the last leg. The first leg starts the plot and joins
 *  no part (R2729), so a part opens at a leg and never at the fleet; the
 *  `loop` flag of the first leg of a plot saved before that rule says
 *  nothing. */
export function loopParts(legs: readonly { loop: boolean; passes: number }[]): LoopPart[] {
  const out: LoopPart[] = [];
  let first = -1;
  for (let i = 1; i < legs.length; i++) {
    const l = legs[i];
    if (!l.loop) { first = -1; continue; }
    if (first < 0) first = i;
    const next = legs[i + 1];
    if (l.passes > 0 || !next || !next.loop) {
      out.push({ open: first - 1, first, last: i, passes: l.passes });
      first = -1;
    }
  }
  return out;
}

function dist(a: PlotPoint, b: PlotPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** The length of each leg from the point before it, in world px. The
 *  first is the joining run from the fleet (R2729). */
export function plotLegLengths(d: PlotDraft): number[] {
  const out: number[] = [];
  let at: PlotPoint = d.from;
  for (const l of d.legs) {
    out.push(dist(at, l));
    at = l;
  }
  return out;
}

/** The length of one round of the plot: every leg after the first, and
 *  the leg back to the opening point of each loop part once. The joining
 *  run is no part of the plot (R2729), so the length is the one that
 *  FLEET · ROUTES states for the saved plot. */
export function plotLength(d: PlotDraft): number {
  let len = plotLegLengths(d).slice(1).reduce((s, x) => s + x, 0);
  for (const p of loopParts(d.legs)) len += dist(d.legs[p.last], d.legs[p.open]);
  return len;
}

/** The plot row: LOOP ▢, UNDO and DONE in the dark face. DONE wears the
 *  cap of enter, which presses it on the map, as `d` does (R3185). */
function plotActs(d: PlotDraft): Action[] {
  const none = d.legs.length === 0;
  return [
    { act: 'plotloop', face: 'quiet', on: d.loop, label: `${d.loop ? S.plot_box_on : S.plot_box_off} ${S.plot_loop}` },
    { act: 'plotundo', face: 'quiet', off: none, label: S.plot_undo },
    { act: 'plotdone', face: 'dark', off: none, label: S.plot_done, key: KEYS.map.menu_focus.label.toUpperCase() },
  ];
}

/** The plot line: PLOT · n LEGS, the fleet and the length, the hint, ✕. */
function plotLine(d: PlotDraft): string {
  return '<div class="cs-line cs-pl">'
    + `<span class="ck-mk cs-route" aria-hidden="true">${esc(S.plot_mark)}</span>`
    + `<span class="cs-nw"><b>${esc(S.plot_legs({ n: d.legs.length }))}</b>`
    + `<i class="cs-k">${esc(S.plot_sub({ name: d.name, px: num(Math.round(plotLength(d))) }))}</i></span>`
    + `<span class="cs-tap">${esc(plotFull(d) ? S.plot_full : S.plot_tap)}</span>`
    + sheetCloseHtml('plotend', S.plot_end_title)
    + '</div>';
}

/** The closed plot sheet: the plot line and the plot row. The grab
 *  handle of the sheet raises it (R2008). */
export function plotClosedHtml(d: PlotDraft, env: CardEnv): string {
  return `<div class="ck cs cs-plot">${plotLine(d)}`
    + `<div class="cs-row">${plotActs(d).map((a) => buttonHtml(a, env)).join('')}</div></div>`;
}

/** The pass count of a part: `×2` as a press that steps it, or `∞` for
 *  the last part with no end. */
function passHtml(p: LoopPart): string {
  if (p.passes === 0) return `<span class="cs-lpk" title="${esc(S.plot_endless_title)}">${esc(S.plot_endless)}</span>`;
  return `<button class="cs-pass" data-act="plotpass" data-n="${p.last}" title="${esc(S.plot_pass_title)}"`
    + ` aria-label="${esc(S.plot_pass_title)}">${esc(S.plot_passes({ n: p.passes }))}</button>`;
}

/** The list of the legs: a grip, the number, the length and ✕, with a bar
 *  at the left edge on the legs of a loop part, and after each part the
 *  leg back to its opening point with the pass count. The first row
 *  states the joining run from the fleet (R2729). `desk` takes the 28 px
 *  rows of a mouse. `season` false draws no drag grip: a match has no
 *  reorder (arcade.md §7.2, R3225). */
export function legRowsHtml(d: PlotDraft, desk = false, season = true): string {
  const lens = plotLegLengths(d);
  const parts = loopParts(d.legs);
  const d1 = desk ? ' d' : '';
  let rows = '';
  let k = 0;
  d.legs.forEach((leg, i) => {
    rows += `<div class="cs-leg${d1}${leg.loop ? ' inl' : ''}" data-leg="${i}">`
      + (season ? `<span class="cs-grip" data-grip="${i}" aria-hidden="true">${esc(S.plot_grip)}</span>` : '<span class="cs-grip"></span>')
      + `<span class="cs-num">${i + 1}</span>`
      + `<span class="cs-ln"><b>${esc(S.plot_leg({ n: i + 1 }))}</b><i>${esc((i === 0 ? S.plot_join_px : S.plot_leg_px)({ px: num(Math.round(lens[i])) }))}</i></span>`
      + `<button class="ck-x" data-act="plotleg" data-n="${i}" title="${esc(S.plot_leg_drop)}"`
      + ` aria-label="${esc(S.plot_leg_drop)}">&#10005;</button></div>`;
    const p = parts[k];
    if (p && p.last === i) {
      k++;
      const back = Math.round(dist(leg, d.legs[p.open]));
      const to = S.plot_back_leg({ n: p.open + 1 });
      rows += `<div class="cs-leg lp inl${d1}"><span class="cs-grip"></span>`
        + `<span class="cs-num">${esc(S.plot_back_mark)}</span>`
        + `<span class="cs-ln"><b>${esc(to)}</b><i>${esc(S.plot_leg_px({ px: num(back) }))}</i></span>`
        + passHtml(p) + '</div>';
    }
  });
  return `<div class="cs-legs">${rows}</div>`;
}

/** The desktop card writes its press with no key cap of its own. */
const buttonEnv: CardEnv = { keycaps: false };

/** SAVE AS PLOT (R2589, round 088 card 09): the course goes to the course
 *  plots of FLEET · ROUTES. A draft with no leg has nothing to save. */
function saveHtml(d: PlotDraft, env: CardEnv): string {
  if (d.legs.length === 0) return '';
  return buttonHtml({ act: 'plotsave', face: 'quiet', label: S.plot_save }, env);
}

/** The name a plot takes when it is saved: the letters of the fleet name,
 *  the apostrophe and the hyphen, cut to `max` letters, or `fallback`
 *  when fewer than `min` remain (the rule of a hull name part, R2587). */
export function plotNameOf(fleetName: string, min: number, max: number, fallback: string): string {
  const name = cutRunes(tidySpaces(fleetName.replace(/[^\p{L}' -]+/gu, ' ')), max).trim();
  return runeCount(name) >= min ? name : fallback;
}

/** The raised plot sheet: the plot line, the legs, and the plot row
 *  pinned to the foot. The grab handle of the sheet lowers it (R2008).
 *  `season` false is the sheet of a match (arcade.md §7.2, R3225): no
 *  SAVE AS PLOT and no reorder, because a match saves no plot. */
export function plotRaisedHtml(d: PlotDraft, env: CardEnv, season = true): string {
  return `<div class="ck cs cs-plot raised">${plotLine(d)}`
    + `<div class="ck-sect"><span class="ck-cap">${esc(S.plot_legs_head)}</span>`
    + (season ? `<i class="cs-hint">${esc(S.plot_reorder)}</i>${saveHtml(d, env)}` : '') + '</div>'
    + legRowsHtml(d, false, season)
    + `<div class="ck-acts">${plotActs(d).map((a) => buttonHtml(a, env)).join('')}</div></div>`;
}

/** A desktop card in plot mode: a LEGS section in its body, and the plot
 *  row in place of the order row. `season` false is the card of a match:
 *  no SAVE AS PLOT and no reorder (R3225). */
export function plotCardSpec(spec: CardSpec, d: PlotDraft, season = true): CardSpec {
  const sect = `<div class="ck-sect cs-legsect"><span class="ck-cap">${esc(S.plot_legs_head)}</span>`
    + `<i class="cs-hint">${esc(S.plot_legs({ n: d.legs.length }))} · ${esc(S.plot_leg_px({ px: num(Math.round(plotLength(d))) }))}</i>`
    + `${season ? saveHtml(d, buttonEnv) : ''}</div>`
    + legRowsHtml(d, true, season);
  return { ...spec, body: (spec.body ?? '') + sect, actions: plotActs(d), more: undefined };
}

/** The legs as the wire sends them (`Waypoint`, R2576): world px, the loop
 *  flag and the pass count. The draft marker `closes` stays behind. */
export function plotWaypoints(legs: readonly PlotLeg[]): PlotLeg[] {
  return legs.map((l) => ({ x: l.x, y: l.y, loop: l.loop, passes: l.passes }));
}
